import { create } from '@bufbuild/protobuf'
import { type Timestamp } from '@bufbuild/protobuf/wkt'
import { type ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { type LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import {
  type PowerType,
  type BgMasterListItem as ProtoBgMasterListItem,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { listBgMasters } from '@ebalance/proto/bffuser/v1/occto_master_service-OcctoMasterService_connectquery'
import {
  ListBgMastersRequestSchema,
  type ListBgMastersResponse,
} from '@ebalance/proto/bffuser/v1/occto_master_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import {
  type CivilDate,
  protoDateToCivilDate,
  requireCivilDate,
} from '~/models/civilDate'

// Frontend display row type. Flattens optional proto fields into
// `undefined`-bearing primitives.
export type BgMasterRow = {
  id: string
  bgCode: string
  bgName: string
  areaCode: AreaCode
  licenseType: LicenseType
  powerType: PowerType | undefined
  memberCount: number
  /** 配下発電所数 (= AC-09、非発電BGは常に0)。 */
  plantCount: number
  updatedBy: string | undefined
  updatedAt: Timestamp | undefined
  /** 適用開始日 (必須)。欠落は契約違反として converter が throw する。 */
  validFrom: CivilDate
  validTo: CivilDate | undefined
}

function convertItem(item: ProtoBgMasterListItem): BgMasterRow {
  return {
    id: item.id,
    bgCode: item.bgCode,
    bgName: item.bgName,
    areaCode: item.areaCode,
    licenseType: item.licenseType,
    powerType: item.powerType,
    memberCount: item.memberCount,
    plantCount: item.plantCount,
    updatedBy: item.updatedBy,
    updatedAt: item.updatedAt,
    validFrom: requireCivilDate(
      protoDateToCivilDate(item.validFrom),
      `BgMasterListItem.validFrom (id=${item.id})`,
    ),
    validTo: protoDateToCivilDate(item.validTo),
  }
}

export function convertListBgMastersResponse(
  resp: ListBgMastersResponse,
): BgMasterRow[] {
  return resp.items.map(convertItem)
}

/**
 * BgMasterListItem[] (= 集約 RPC レスポンスの field 型) を BgMasterRow[] に変換する。
 * 集約 RPC (GetGenerationSalesPlanMasterFormOptions 等) は BgMasterListItem[] を
 * 直接フィールドとして返すため、convertListBgMastersResponse の代わりに使う。
 */
export function convertBgMasterListItems(
  items: ProtoBgMasterListItem[],
): BgMasterRow[] {
  return items.map(convertItem)
}

/**
 * @param options.throwOnError 未指定なら QueryClient 既定 (= error boundary へ飛ばす) に従う。
 * @param options.enabled 未指定なら常に取得する。
 */
export function useListBgMasters(options?: {
  throwOnError?: boolean
  enabled?: boolean
}): UseQueryResult<ListBgMastersResponse, ConnectError> {
  return useQuery(listBgMasters, create(ListBgMastersRequestSchema, {}), {
    // BG マスタは変化頻度が低く複数画面から参照されるため長め cache
    // (useListMembers failSafe と同方針)。
    staleTime: 5 * 60 * 1000,
    // 未指定の key は渡さない (`undefined` を渡すと QueryClient 既定を上書きする)。
    ...(options?.throwOnError !== undefined
      ? { throwOnError: options.throwOnError }
      : {}),
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
  })
}
